import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    Alert,
    Paper,
    SvgIconProps
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Psychology as PsychologyIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Settings as SettingsIcon,
    TrendingUp as TrendingUpIcon,
    Warning as WarningIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useDataProvider, useNotify } from 'react-admin';
import { RawEventData, EventItem } from '../real-tickets/models/event';
import { buildFilterObject, fetchPreFilteringRules, SearchEventTable, PreFilteringRule, EventSearch } from '../real-tickets/components/SearchEvents';
import { loadSourceType, loadWhitelistCategory } from '../../utils/common';
import MDateControl from '../../components/controls/MDateControl';
import MSelect from '../../components/controls/MSelect';
import MAutoComplete from '../../components/controls/MAutoComplete';
import { SingleEventAnalysisTab, BulkAnalysisTab } from '../real-tickets/components/tabs';
import PreFilterConfigDialog, { CategoryChoice } from '../real-tickets/components/PreFilterConfigDialog';
import {
    WhitelistCategories,
    WhitelistCategory,
    ModelPerformance,
    FeatureImportance
} from '../real-tickets/AnalysisHistorical/types';
import { SeatRecommendation } from '../real-tickets/models/seat';
import { AIScoring, SingleEventAnalysisTabHandle, ExtendedDataProvider } from '../real-tickets/components/tabs/utils/SingleEventAnalysisTab.types';
import { BulkAnalysisTabHandle, BulkAnalysisResult, RecommendationCount } from '../real-tickets/components/tabs/BulkAnalysisTab';
import { AgGridHandle } from '../../components/ag-grid/share/type';

/**
 * Returns color string for score chip based on value.
 * @param score - The score value to evaluate
 * @returns Color identifier for the chip
 */
const getScoreColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 70) return 'success';
    if (score >= 55) return 'warning';
    return 'error';
};

/**
 * Returns icon component for recommendation level.
 * @param level - The recommendation level string
 * @param props - Additional props to pass to the icon component
 * @returns Icon component for the recommendation level
 */
const getRecommendationIcon = (level: string, props?: SvgIconProps): JSX.Element => {
    switch (level) {
        case 'Strong Buy': return <CheckCircleIcon color='success' {...props} />;
        case 'Buy': return <TrendingUpIcon color='info' {...props} />;
        case 'Hold': return <WarningIcon color='warning' {...props} />;
        case 'Ignore': return <ErrorIcon color='error' {...props} />;
        default: return <InfoIcon {...props} />;
    }
};
/**
 * AIAnalysisPage
 * Main component for AI Ticket Analysis Center.
 * Provides event search, single analysis, bulk analysis, and model performance tabs.
 * Integrates with backend AI endpoints for scoring and recommendations.
 */
const AIAnalysisPage: React.FC = () => {
    const refSearchEvent = useRef<AgGridHandle>(null);
    const [activeTab, setActiveTab] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [initializing, setInitializing] = useState<boolean>(true);
    const [initError, setInitError] = useState<string | null>(null);
    const [eventSearch, setEventSearch] = useState<EventSearch>({
        eventName: '',
        venue: '',
        dateFrom: new Date().toISOString(),
        dateTo: null,
        eventType: '',
        marketType: ''
    });
    // Event search results state
    const [searchResults, setSearchResults] = useState<any>({});
    const [marketTypes, setMarketTypes] = useState<string[]>([]);
    const [whitelistCategories, setWhitelistCategories] = useState<WhitelistCategories>({ eventTypes: [], categories: [] });

    // Model performance states
    const [modelPerformance, setModelPerformance] = useState<ModelPerformance | null>(null);
    const [featureImportance, setFeatureImportance] = useState<FeatureImportance[]>([]);
    const [trainingDialog, setTrainingDialog] = useState<boolean>(false);
       
    // Single Analysis states
    const [analysisTarget, setAnalysisTarget] = useState<EventItem | null>(null);
    const [analysisBulkTarget, setAnalysisBulkTarget] = useState<EventItem | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
    const [seatRecommendations, setSeatRecommendations] = useState<SeatRecommendation[]>([]);
    const [analysisCompleted, setAnalysisCompleted] = useState<boolean>(false);
    const [aiScoring, setAiScoring] = useState<AIScoring | null>(null);
    const singleAnalysisRef = useRef<SingleEventAnalysisTabHandle>(null);

    // Bulk Analysis states
    const [selectedEvents, setSelectedEvents] = useState<RawEventData[]>([]);
    const [bulkAnalysis, setBulkAnalysis] = useState<BulkAnalysisResult[]>([]);
    const bulkAnalysisRef = useRef<BulkAnalysisTabHandle>(null);
    const [recommendationCount, setRecommendationCount] = useState<RecommendationCount[]>([
        { name: 'Strong Buy', count: 0 },
        { name: 'Buy', count: 0 },
        { name: 'Hold', count: 0 },
        { name: 'Ignore', count: 0 }
    ]);

    // Pre-filter configuration dialog states
    const [reFilterDialogOpen, setReFilterDialogOpen] = useState<boolean>(false);
    const [preFilteringRules, setPreFilteringRules] = useState<PreFilteringRule[]>([]);

    // Loading state for initial data load
    const [isLoadingInitialData, setIsLoadingInitialData] = useState<boolean>(true);
    const [initialDataError, setInitialDataError] = useState<string | null>(null);

    // Data provider and notification hook
    const dataProvider = useDataProvider() as unknown as ExtendedDataProvider;
    const notify = useNotify();
    
   
    

    /**
     * Searches for events using the AI controller.
     * Updates searchResults state with returned events.
     * Resets analysis state.
     * Integrates pre-filtering rules from scoring configuration.
     */
    const searchEvents = async (): Promise<void> => {
        setSeatRecommendations([]);
        try {
            let odataSearch = buildFilterObject(eventSearch, preFilteringRules);
            refSearchEvent.current?.customFilterSearch(odataSearch);
        } catch (error) {
            notify('Failed to search events: ' + (error instanceof Error ? error.message : 'Unknown error'), { type: 'error' });
        }
    };

    /**
     * Updates search results and clears selected events.
     * Called by the AgGrid component when search results change.
     * @param result - Array of event objects from the search
     * @param total - Total number of events matching the search
     * @param pageIndex - Current page index
     * @param pageSizeInput - Number of events per page
     */
    const updateSearchResult = (result: RawEventData[], total: number, pageIndex: number, pageSizeInput: number): void => {
        setSearchResults({ data: result, total, pageIndex, pageSize: pageSizeInput, pageCount: Math.ceil(total / pageSizeInput) });
        // Clear selected events when search results change
        setSelectedEvents([]);
    };

    /**
     * Opens the re-filter configuration dialog
     */
    const openReFilterDialog = (): void => {
        setReFilterDialogOpen(true);
    };

    /**
     * Closes the re-filter configuration dialog
     */
    const closeReFilterDialog = (): void => {
        setReFilterDialogOpen(false);
    };

    /**
     * Handles saving the re-filter configuration from the dialog
     * @param newRules - Array of filter rules from the dialog
     */
    const handleReFilterSave = (newRules: PreFilteringRule[]): void => {
        const mergedRules = preFilteringRules.map(rule => {
            const found = newRules.find(nr => nr.variable === rule.variable);
            if (found) {
                return {
                    ...rule,
                    value: found.value
                };
            }
            return rule;
        });
        // Add any new rules that were not in the previous set
        newRules.forEach(nr => {
            if (!mergedRules.find(r => r.variable === nr.variable)) {
                mergedRules.push(nr);
            }
        });
        // Update state with merged rules
        setPreFilteringRules(mergedRules);
    };

    /**
     * Analyzes a single event using the real-ticket-api /score-event endpoint.
     * This is the new version that uses the real-ticket-api service directly.
     * @param event - The event object to analyze.
     */
    const analyzeEventWithRealTicketAPI = async (event: EventItem): Promise<void> => {
        setAnalysisTarget(event);
        setSelectedEvent(event);
        // Switch to analysis tab (1)
        setActiveTab(1);
    };

    useEffect(() => {
        // Fetch AI scoring and seat recommendations when switching to Single Analysis tab
        if (activeTab === 1 && singleAnalysisRef?.current) {
            const runAnalysis = async () => {
                setAnalysisCompleted(false);
                try {
                    if (!singleAnalysisRef.current) {
                        throw new Error('Single analysis ref not available');
                    }
                    if (!analysisTarget && !analysisBulkTarget) return;

                    let scoringData: AIScoring;
                    
                    // Check if we should fetch new data or use existing bulk analysis result
                    if (analysisTarget) {
                        // Original fetch logic when analysisTarget is set
                        scoringData = await singleAnalysisRef.current.fetchScoringData(selectedEvent!) || {};
                    } else if (analysisBulkTarget) {
                        // Check if we have bulk analysis data for this event
                        const bulkResult = bulkAnalysis.find(x => x.siteId === analysisBulkTarget.siteId);
                        if (bulkResult) {
                            // Use bulk analysis result directly without fetching
                            scoringData = AIScoring.fromBulkAnalysis(bulkResult);
                            scoringData.prepareData = analysisBulkTarget.prepareData || null;
                            scoringData.analysisId = Number(analysisBulkTarget.analysisId || 0);
                        } else {
                            // No bulk analysis data, return early
                            return;
                        }
                    } else {
                        // No event selected, return early
                        return;
                    }
                    
                    // Update state with scoring data for display in SingleAnalysisTab immediately
                    setAiScoring(scoringData);
                    // Get the minimum event score threshold from the pre-filtering rules
                    // Extract the threshold from the unified rules array by finding the rule with variable 'minEventScore'
                    const minEventScoreRule = preFilteringRules.find(rule => rule.variable === 'minEventScore');
                    const minEventScoreThreshold = minEventScoreRule ? minEventScoreRule.value : 60; // Default to 60 if not set
                    
                    // Also get seat recommendations from the original AI service if overallScore > threshold
                    // Only fetch seat recommendations if we have prepareData and analysisId (from fresh analysis, not bulk),
                    // First time analysis will not have seat prepareData, analysisId (view from bulk), it takes time to get them in next render.
                    const seatResponse = scoringData.scores &&
                                        scoringData.scores.overallScore > minEventScoreThreshold &&
                                        scoringData.prepareData &&
                                        scoringData.analysisId
                        ? scoringData.prepareData.haveSeatAnalysis
                            ? await singleAnalysisRef.current.getSeatRecommendationsHistory(scoringData.analysisId)
                            : await singleAnalysisRef.current.fetchSeatRecommendations(EventItem.fromPrepareData(scoringData.prepareData), scoringData.analysisId)
                        : [];
                    setSeatRecommendations(seatResponse ?? []);
                    if (scoringData.prepareData) {
                        // Update state with seat recommendations for display in SingleAnalysisTab, final results will be shown after analysis is completed
                        notify('Event analysis completed using Real Ticket API', { type: 'success' });
                    }
                    // Only update search results if this was a fresh analysis (not viewing bulk data)
                    if (analysisTarget) {
                        setSearchResults((prevResults: any) => {
                            if (!prevResults.data) return prevResults;
                            const updatedResults = prevResults.data.map((item: any) => {
                                if (item.siteId === selectedEvent?.siteId) {
                                    return { ...item, score: scoringData.scores?.overallScore, analysisTimestamp: scoringData.analysisTimestamp };
                                }
                                return item;
                            });
                            return { ...prevResults, data: updatedResults };
                        });
                    }
                } catch (error) {
                    setAiScoring(null);
                    setSeatRecommendations([]);
                    console.error('Real Ticket API analysis error:', error);
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    notify('Failed to analyze event with Real Ticket API: ' + errorMessage, { type: 'error' });
                } finally {
                    setAnalysisTarget(null);
                    setAnalysisBulkTarget(null);
                    // Mark analysis as completed to show results
                    setAnalysisCompleted(true);
                }
            };
            runAnalysis();
        }
    }, [activeTab, analysisTarget, analysisBulkTarget, selectedEvent, bulkAnalysis, preFilteringRules]);

    /**
     * Handles selection changes in the AgGrid table.
     * Updates the selectedEvents state with the currently selected event objects.
     * @param selectedRows - Array of selected event objects from AgGrid
     */
    const handleEventSelectionChanged = (selectedRows: RawEventData[]): void => {
        setSelectedEvents(selectedRows);
    };

    /**
     * Analyzes a single event using AI scoring and seat recommendation endpoints.
     * Updates selectedEvent, aiScoring, seatRecommendations, and analysisCompleted states.
     * Switches to Single Analysis tab.
     * @param event - The event object to analyze.
     */
    const analyzeEvent = async (event: RawEventData): Promise<void> => {
        // Use the new Real Ticket API version by default
        await analyzeEventWithRealTicketAPI(EventItem.fromRawEventData(event));
    };

    /**
     * Runs bulk analysis on selected events.
     * Updates bulkAnalysis state and switches to Bulk Analysis tab.
     */
    const runBulkAnalysis = async (): Promise<void> => {
        if (selectedEvents.length === 0) {
            notify('No events selected. Please select events to analyze.', { type: 'warning' });
            return;
        }

        setLoading(true);
        try {
            // Use the selected events for bulk analysis
            setActiveTab(2); // Switch to bulk analysis tab
            // Wait a bit for component to mount and ref to be set
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (!bulkAnalysisRef.current) {
                throw new Error('Bulk analysis ref not available');
            }
            
            // Use the selected events for bulk analysis
            const bulkResp = await bulkAnalysisRef.current.runBulkAnalysis(selectedEvents);
            setBulkAnalysis(bulkResp);
            //update score of event in aggrid
            const updatedResults = searchResults.data.map((item: any) => {
                let scoringData = bulkResp.find(bulkItem => bulkItem.siteId === item.siteId);
                if (scoringData) {
                    return { ...item, score: scoringData.scores.overallScore, isAnalyzed: true };
                }
                return item;
            });

            setSearchResults({ ...searchResults, data: updatedResults });
        } catch (error) {
            setBulkAnalysis([]);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            notify('Failed to run bulk analysis: ' + errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Loads model performance metrics from backend.
     * Updates modelPerformance state.
     */
    const loadModelPerformance = async (): Promise<void> => {
        try {
            const response = await dataProvider.fetchData<ModelPerformance>('TicketSelection', 'model-performance');
            setModelPerformance(response.data);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            notify('Failed to load model performance: ' + errorMessage, { type: 'error' });
        }
    };

    /**
     * Loads feature importance data from backend.
     * Updates featureImportance state.
     */
    const loadFeatureImportance = async (): Promise<void> => {
        try {
            const response = await dataProvider.fetchData<FeatureImportance[]>('TicketSelection', 'feature-importance');
            setFeatureImportance(response.data);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            notify('Failed to load feature importance: ' + errorMessage, { type: 'error' });
        }
    };

    /**
     * Retrains the AI model using the latest 2 years of data.
     * Updates model performance after retraining.
     */
    const retrainModel = async (): Promise<void> => {
        setLoading(true);
        try {
            await dataProvider.postData('TicketSelection', 'retrain-model', {
                trainingStartDate: dayjs().subtract(2, 'year').format('YYYY-MM-DD'),
                trainingEndDate: dayjs().format('YYYY-MM-DD'),
                validateOnHoldout: true
            });

            notify('Model retraining completed successfully', { type: 'success' });
            await loadModelPerformance();
            setTrainingDialog(false);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            notify('Failed to retrain model: ' + errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Formats a number as a percent string.
     * If value <= 1, multiplies by 100.
     * @param value - Number to format
     * @returns Formatted percentage string
     */
    const formatPercent = (value: number | undefined): string => {
        if (typeof value !== 'number') return '';
        return value <= 1 ? (value * 100).toFixed(1) + '%' : value.toFixed(1) + '%';
    };

    /**
     * Handles side effects when activeTab changes.
     */
    useEffect(() => {
        if (activeTab === 3) {
            loadModelPerformance();
            loadFeatureImportance();
        }
    }, [activeTab]);

    /**
     * Handle after render completed and initialize cached data
     */
    useEffect(() => {
        loadSourceType(dataProvider, (response: string[]) => {
            setMarketTypes(response);
        });
        loadWhitelistCategory(dataProvider, (response: WhitelistCategory[]) => {
            let wlEventTypes = [...new Set(response.map(obj => obj.eventType))];
            setWhitelistCategories({ eventTypes: wlEventTypes, categories: response });
        });

        // Load initial data including cached data and pre-filtering rules
        const loadData = async (): Promise<void> => {
            try {
                setIsLoadingInitialData(true);
                setInitialDataError(null);

                // Call LoadCachedData endpoint to ensure cached data is loaded
                try {
                    await dataProvider.fetchData('RTP_Event', 'LoadCachedDataEndpoint');
                    notify('Cached data loaded successfully', { type: 'success' });
                    setInitializing(false);
                    setInitError(null);
                } catch (cacheError) {
                    const errorMessage = cacheError instanceof Error ? cacheError.message : 'Unknown error';
                    notify('Error loading cached data: ' + errorMessage, { type: 'error' });
                    // Continue loading other data even if cache load fails
                    setInitializing(false);
                    setInitError('Failed to load cached data: ' + errorMessage);
                }
                // Fetch pre-filtering rules from the Real Ticket API
                let rules = await fetchPreFilteringRules();
                setPreFilteringRules(rules);

                setIsLoadingInitialData(false);
            } catch (error) {
                console.error('Error loading initial data:', error);
                const errorMessage = error instanceof Error ? error.message : 'Failed to load initial data';
                setInitialDataError(errorMessage);
                setIsLoadingInitialData(false);
                notify('Error loading initial data: ' + errorMessage, { type: 'error' });
            }
        };

        loadData();
    }, []);

    /**
     * Handle preFilter rules changes
     */
    useEffect(() => {
        let isCheckDateFrom = preFilteringRules.find(x => x.variable === 'time_left_days');
        if (isCheckDateFrom?.value !== null && isCheckDateFrom?.value !== undefined && isCheckDateFrom?.value >= 0) {
            let dateFromValue = dayjs().startOf('day').add(isCheckDateFrom.value, 'day').toISOString();
            let dateToValue = dayjs(dateFromValue).add(2, 'year').toISOString();
            setEventSearch(prev => ({ ...prev, dateFrom: dateFromValue, dateTo: dateToValue }));
        } else {
            setEventSearch(prev => ({ ...prev, dateFrom: null, dateTo: null }));
        }
    }, [preFilteringRules]);

    const getCategoriesWithType = (): CategoryChoice[] => {
        let categoryList = whitelistCategories.categories;
        let duplicates: WhitelistCategory[] = [];
        let seen: string[] = [];
        categoryList.forEach(obj => {
            if (seen.includes(obj.name)) duplicates.push(obj);
            else seen.push(obj.name);
        });
        let temp = categoryList.filter(ctg => eventSearch.eventType ? ctg.eventType === eventSearch.eventType : ctg)
            .map(x => ({ id: x.id, name: duplicates.some(dup => dup.name === x.name) ? x.name + ' - ' + x.eventType + `[${x.id}]` : x.name }));
        return temp as CategoryChoice[];
    };

    // Show loading spinner while initial data is loading
    if (isLoadingInitialData) {
        return (
            <Box sx={{ width: '100%', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading cached data and initializing analysis center...
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Show error state if initial data loading failed
    if (initialDataError) {
        return (
            <Box sx={{ width: '100%', p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="h6">Failed to load initial data</Typography>
                    <Typography>{initialDataError}</Typography>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        sx={{ mt: 2 }}
                    >
                        Retry
                    </Button>
                </Alert>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* Show loading spinner while initializing */}
            {initializing && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh',
                    gap: 2
                }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" color="textSecondary">
                        Loading AI Analysis System...
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Initializing cached data and preparing the analysis engine
                    </Typography>
                </Box>
            )}

            {/* Show error message if initialization failed */}
            {!initializing && initError && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh',
                    gap: 2,
                    p: 3
                }}>
                    <ErrorIcon color="error" sx={{ fontSize: 60 }} />
                    <Typography variant="h6" color="error">
                        Initialization Error
                    </Typography>
                    <Alert severity="error" sx={{ maxWidth: 600 }}>
                        {initError}
                    </Alert>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        startIcon={<RefreshIcon />}
                    >
                        Refresh Page
                    </Button>
                </Box>
            )}

            {/* Main content - only render after initialization is complete and successful */}
            {!initializing && !initError && (
                <Box sx={{ width: '100%', p: 3 }} className="ai-analysis-tab">
                    {/* Header */}
                    <Paper elevation={1} sx={{ p: 1.5, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PsychologyIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="h6" component="h1">
                                        Ticket Analysis Center
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Intelligent event scoring and seat recommendations
                                    </Typography>
                                </Box>
                            </Box>
                            <Box>
                                <Tooltip title="Refresh Data">
                                    <IconButton size="small" onClick={() => window.location.reload()}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Model Settings">
                                    <IconButton size="small" onClick={() => setTrainingDialog(true)}>
                                        <SettingsIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Navigation Tabs */}
                    <Card>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            variant="fullWidth"
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Event Search" icon={<SearchIcon />} />
                            <Tab label="Single Analysis" icon={<AssessmentIcon />} />
                            <Tab label="Bulk Analysis" icon={<TrendingUpIcon />} />
                            <Tab label="Model Performance" icon={<HistoryIcon />} />
                        </Tabs>

                        {/* Tab 1: Event Search */}
                        {activeTab === 0 && <Grid container spacing={3} key='event-search'>
                            <Grid size={2}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Search Events
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={openReFilterDialog}
                                                startIcon={<SettingsIcon />}
                                                size="small"
                                                fullWidth
                                            >
                                                Pre-filter Config
                                            </Button>
                                            <TextField
                                                label="Site ID"
                                                value={eventSearch.siteId ?? ''}
                                                onChange={(e) => setEventSearch(prev => ({ ...prev, siteId: e.target.value }))}
                                                onKeyDown={(e) => e.key === 'Enter' && searchEvents()}
                                                fullWidth
                                                size="small"
                                            />
                                            <TextField
                                                label="Event Name"
                                                value={eventSearch.eventName}
                                                onChange={(e) => setEventSearch(prev => ({ ...prev, eventName: e.target.value }))}
                                                onKeyDown={(e) => e.key === 'Enter' && searchEvents()}
                                                fullWidth
                                                size="small"
                                            />
                                            <TextField
                                                label="Venue"
                                                value={eventSearch.venue}
                                                onChange={(e) => setEventSearch(prev => ({ ...prev, venue: e.target.value }))}
                                                onKeyDown={(e) => e.key === 'Enter' && searchEvents()}
                                                fullWidth
                                                size="small"
                                            />
                                            <MSelect
                                                label={'Market Type'}
                                                value={eventSearch.marketType}
                                                choices={marketTypes}
                                                onChange={(ev: any) => setEventSearch(prev => ({ ...prev, marketType: ev }))} />
                                            <MSelect
                                                label={'Event Type'}
                                                value={eventSearch.eventType}
                                                choices={whitelistCategories.eventTypes}
                                                onChange={(ev: any) => setEventSearch(prev => ({ ...prev, eventType: ev }))} />
                                            <MAutoComplete id="at_category_name" name='category_name' variant={'standard'} label={'Category Name'}
                                                choices={getCategoriesWithType()} className="" onChange={(values: any) => {
                                                    setEventSearch(prev => ({ ...prev, categoryIds: values.length > 0 ? values.map((x: any) => x.id) : null }));
                                                }} />
                                            <MDateControl
                                                label={'Date From'}
                                                defaultValue={eventSearch.dateFrom}
                                                value={eventSearch.dateFrom}
                                                onChange={(newValue: any) => setEventSearch(prev => ({ ...prev, dateFrom: newValue }))}
                                            />
                                            <MDateControl
                                                label={'Date To'}
                                                defaultValue={eventSearch.dateTo}
                                                value={eventSearch.dateTo}
                                                onChange={(newValue: any) => setEventSearch(prev => ({ ...prev, dateTo: newValue }))}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={searchEvents}
                                                disabled={loading}
                                                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                                            >
                                                {loading ? 'Searching...' : 'Search'}
                                            </Button>
                                            {selectedEvents.length > 0 && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={runBulkAnalysis}
                                                    disabled={loading}
                                                    startIcon={<TrendingUpIcon />}
                                                >
                                                    Bulk Analyze ({selectedEvents.length})
                                                </Button>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={10} sx={{ paddingTop: '20px', paddingBottom: '40px' }}>
                                <SearchEventTable
                                    list="RTP_Event"
                                    filterValue={buildFilterObject(eventSearch, preFilteringRules)}
                                    refInput={refSearchEvent}
                                    analyzeEvent={analyzeEvent}
                                    searchResults={searchResults.data}
                                    paginationData={{ page: searchResults.pageIndex, pageSize: searchResults.pageSize, total: searchResults.total, pageCount: searchResults.pageCount }}
                                    updateSearchResult={updateSearchResult}
                                    onSelectionChanged={handleEventSelectionChanged}
                                    selectedRowKeys={selectedEvents.map(x => ({ id: x.id }))} />
                            </Grid>
                        </Grid>}

                        {/* Tab 2: Single Event Analysis */}
                        {activeTab === 1 && (
                            <SingleEventAnalysisTab
                                ref={singleAnalysisRef}
                                selectedEvent={selectedEvent}
                                setSelectedEvent={setSelectedEvent}
                                analysisCompleted={analysisCompleted}
                                aiScoring={aiScoring}
                                setAiScoring={setAiScoring}
                                seatRecommendations={seatRecommendations}
                                setSeatRecommendations={setSeatRecommendations}
                                preFilteringRules={preFilteringRules}
                                setSearchResults={setSearchResults}
                            />
                        )}

                        {/* Tab 3: Bulk Analysis */}
                        {activeTab === 2 && (
                            <Grid>
                                <BulkAnalysisTab
                                    ref={bulkAnalysisRef}
                                    isLoading={loading}
                                    bulkAnalysis={bulkAnalysis}
                                    selectedEvents={selectedEvents.map(ev => EventItem.fromRawEventData(ev))}
                                    getRecommendationIcon={getRecommendationIcon}
                                    getScoreColor={getScoreColor}
                                    recommendationCount={recommendationCount}
                                    setRecommendationCount={setRecommendationCount}
                                    setActiveTab={setActiveTab}
                                    setAnalysisBulkTarget={setAnalysisBulkTarget}
                                    setSelectedEvent={setSelectedEvent}
                                />
                            </Grid>
                        )}

                        {/* Tab 4: Model Performance */}
                        {activeTab === 3 && (
                            <Grid container spacing={3}>
                                {/* Model Performance Metrics */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Model Performance
                                            </Typography>
                                            {modelPerformance ? (
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Accuracy</Typography>
                                                        <Typography variant="h6">
                                                            {formatPercent(modelPerformance.accuracyScore)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Precision</Typography>
                                                        <Typography variant="h6">
                                                            {formatPercent(modelPerformance.precisionScore)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Recall</Typography>
                                                        <Typography variant="h6">
                                                            {formatPercent(modelPerformance.recallScore)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">F1 Score</Typography>
                                                        <Typography variant="h6">
                                                            {formatPercent(modelPerformance.f1Score)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Typography variant="caption" color="textSecondary">
                                                            Model Version: {modelPerformance.modelVersion} |
                                                            Training Date: {dayjs(modelPerformance.trainingDate).format('MMM DD, YYYY')}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            ) : (
                                                <Typography>Loading performance metrics...</Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Feature Importance */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Feature Importance
                                            </Typography>
                                            {featureImportance?.length > 0 ? (
                                                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                    {featureImportance.slice(0, 8).map((feature) => (
                                                        <Box key={feature.featureName} sx={{ mb: 2 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography variant="body2">
                                                                    {feature.featureName}
                                                                </Typography>
                                                                <Typography variant="caption">
                                                                    {(feature.importanceScore * 100).toFixed(1)}%
                                                                </Typography>
                                                            </Box>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={feature.importanceScore * 100}
                                                                sx={{ height: 6, borderRadius: 3 }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography>Loading feature importance...</Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}
                    </Card>

                    {/* Pre-filter Configuration Dialog */}
                    <PreFilterConfigDialog
                        open={reFilterDialogOpen}
                        onClose={closeReFilterDialog}
                        preFilteringRules={preFilteringRules}
                        onSave={handleReFilterSave}
                        getCategoriesWithType={getCategoriesWithType}
                    />

                    {/* Model Training Dialog */}
                    <Dialog open={trainingDialog} onClose={() => setTrainingDialog(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Model Training & Settings</DialogTitle>
                        <DialogContent>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Retraining the model will use the latest 2 years of data to improve predictions.
                                This process may take several minutes.
                            </Alert>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Current model performance and recommended actions will be displayed after training.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setTrainingDialog(false)}>Cancel</Button>
                            <Button
                                onClick={retrainModel}
                                variant="contained"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <SettingsIcon />}
                            >
                                {loading ? 'Training...' : 'Retrain Model'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}
        </LocalizationProvider>
    );
};

export default AIAnalysisPage;
export { getRecommendationIcon, getScoreColor };